import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import "../../styles/form.css";
import { useForm, SubmitHandler } from "react-hook-form";
import { Container } from "../../types/Container";
import { InputTextField } from "../FormFields/RenderInputTextField";
import { NumberField } from "../FormFields/RenderNumberField";
import { SelectField } from "../FormFields/RenderSelectField";
import { DateTimeField } from "../FormFields/RenderDateTimeField";
import { SelectMultipleField } from "../FormFields/RenderSelectMultipleField";
import GetTerminalOptions from "../FormHelpers/TeminalSelectOptions";
import GetTerminalCallOptions from "../FormHelpers/TerminalCallSelectOptions";
import GetVesselOptions from "../FormHelpers/VesselSelectOptions";
import GetLabelOptions from "../FormHelpers/LabelSelectOptions";
import { TerminalCall } from "../../types/TerminalCall";
import { findObjectById, formatDateToString } from "../Helpers.tsx";
import { useTranslation } from "react-i18next";

import { createContainer, removeContainer } from "../../data/containers";
import { MultipleFileField } from "../FormFields/RenderMultipleFileField";
import GetVoyageOptions from "../FormHelpers/VoyageSelectOptions.tsx";
import { selectedFilteredVoyage } from "../../selectors/filterSelector.js";
import { updateSelectedContainer } from "../../data/selectedcontainers.js";
import { AutocompleteField } from "../FormFields/RenderAutocompleteField.tsx";
import { CheckboxField } from "../FormFields/RenderCheckboxField.tsx";
import { hideModal, showChildModal, hideChildModal } from "../../data/modal";
import {
  Alert,
  Box,
  Button,
  Container as MuiContainer,
  Divider,
  Grid,
  Typography,
} from "@mui/material";

import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

dayjs.extend(isSameOrAfter);

const FIELD_CHANGE_EVENT_TYPE = "change";

const ContainerForm = ({
  container,
  terminals,
  vessels,
  labels,
  selectedVessel,
  selectedLoadingTerminalCall,
  selectedLoadingTerminal,
  selectedUnloadingTerminal,
  callback,
  parentVessel,
  voyages,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    terminalCalls: { data: terminalCalls },
    containerTypes: { data: containerTypes },
    modal: { childModal: childModal },
  } = useSelector((state: any) => ({
    terminalCalls: state.selectedTerminalCalls,
    containerTypes: state.containerTypes,
    modal: state.modal,
  }));

  const { voyage } = useSelector(selectedFilteredVoyage);

  const containerData = container ?? {
    ...new Container(),
    vessel: selectedVessel ? selectedVessel["@id"] : undefined,
    loadingTerminalCall: selectedLoadingTerminalCall,
    loadingTerminal: selectedLoadingTerminalCall
      ? selectedLoadingTerminalCall.terminal
      : selectedLoadingTerminal,
    unloadingTerminal: selectedUnloadingTerminal,
    voyage: voyage?.["@id"] ?? null,
  };

  const {
    handleSubmit,
    control,
    formState,
    watch,
    setValue,
    register,
  } = useForm({
    defaultValues: containerData,
  });

  const { isSubmitting } = formState;

  const initialLoadingTerminalCalls = containerData.loadingTerminalCall
    ? [
        findObjectById(
          terminalCalls,
          containerData.loadingTerminalCall["@id"]
            ? containerData.loadingTerminalCall["@id"]
            : containerData.loadingTerminalCall
        ),
      ]
    : [];
  const initialUnloadingTerminalCalls = containerData.unloadingTerminalCall
    ? [
        findObjectById(
          terminalCalls,
          containerData.unloadingTerminalCall["@id"]
            ? containerData.unloadingTerminalCall["@id"]
            : containerData.unloadingTerminalCall
        ),
      ]
    : [];

  const [loadingTerminalCalls, setLoadingTerminalCalls] = useState(
    initialLoadingTerminalCalls
  );
  const [unloadingTerminalCalls, setUnloadingTerminalCalls] = useState(
    initialUnloadingTerminalCalls
  );

  const filterTerminalLoadingCalls = (container: Container) => {
    return terminalCalls.filter((terminalCall: TerminalCall) => {
      const requirementsObtained: boolean[] = [];

      // VESSEL REQUIREMENT: container.vessel and/or parentVessel
      const vesselIds: string[] = [];

      if (container.vessel) {
        const vId =
          container.vessel && container.vessel["@id"]
            ? container.vessel["@id"]
            : container.vessel;
        if (vId) {
          vesselIds.push(vId.toString());
        }
      }

      if (parentVessel) {
        const pvId =
          parentVessel && parentVessel["@id"]
            ? parentVessel["@id"]
            : parentVessel;
        if (pvId) {
          vesselIds.push(pvId.toString());
        }
      }

      if (vesselIds.length > 0) {
        const matchesVessel = vesselIds.some((vId) => {
          const tcVessel =
            terminalCall.vessel && terminalCall.vessel["@id"]
              ? terminalCall.vessel["@id"]
              : terminalCall.vessel;
          return tcVessel && tcVessel.toString() === vId;
        });
        requirementsObtained.push(matchesVessel);
      }

      // LOADING TERMINAL REQUIREMENT
      if (container.loadingTerminal) {
        const terminalId =
          container.loadingTerminal && container.loadingTerminal["@id"]
            ? container.loadingTerminal["@id"]
            : container.loadingTerminal;

        const tcTerminal =
          terminalCall.terminal && terminalCall.terminal["@id"]
            ? terminalCall.terminal["@id"]
            : terminalCall.terminal;

        requirementsObtained.push(
          !!terminalId &&
            !!tcTerminal &&
            tcTerminal.toString() === terminalId.toString()
        );
      }

      // LOADING TERMINAL CLOSURE REQUIREMENT
      if (container.loadingTerminalClosure) {
        requirementsObtained.push(
          !!terminalCall.planned &&
            dayjs(terminalCall.planned).isSameOrAfter(
              container.loadingTerminalClosure
            )
        );
      }

      if (requirementsObtained.length > 0) {
        return requirementsObtained.every((requirement) => requirement === true);
      }

      return false;
    });
  };

  const filterTerminalUnloadingCalls = (container: Container) => {
    return terminalCalls.filter((terminalCall: TerminalCall) => {
      const requirementsObtained: boolean[] = [];

      // VESSEL REQUIREMENT: container.vessel and/or parentVessel
      const vesselIds: string[] = [];

      if (container.vessel) {
        const vId =
          container.vessel && container.vessel["@id"]
            ? container.vessel["@id"]
            : container.vessel;
        if (vId) {
          vesselIds.push(vId.toString());
        }
      }

      if (parentVessel) {
        const pvId =
          parentVessel && parentVessel["@id"]
            ? parentVessel["@id"]
            : parentVessel;
        if (pvId) {
          vesselIds.push(pvId.toString());
        }
      }

      if (vesselIds.length > 0) {
        const matchesVessel = vesselIds.some((vId) => {
          const tcVessel =
            terminalCall.vessel && terminalCall.vessel["@id"]
              ? terminalCall.vessel["@id"]
              : terminalCall.vessel;
          return tcVessel && tcVessel.toString() === vId;
        });
        requirementsObtained.push(matchesVessel);
      }

      // UNLOADING TERMINAL REQUIREMENT
      if (container.unloadingTerminal) {
        const terminalId =
          container.unloadingTerminal && container.unloadingTerminal["@id"]
            ? container.unloadingTerminal["@id"]
            : container.unloadingTerminal;

        const tcTerminal =
          terminalCall.terminal && terminalCall.terminal["@id"]
            ? terminalCall.terminal["@id"]
            : terminalCall.terminal;

        requirementsObtained.push(
          !!terminalId &&
            !!tcTerminal &&
            tcTerminal.toString() === terminalId.toString()
        );
      }

      // UNLOADING TERMINAL CLOSURE REQUIREMENT
      if (container.unloadingTerminalClosure) {
        requirementsObtained.push(
          !!terminalCall.planned &&
            dayjs(terminalCall.planned).isSameOrAfter(
              container.unloadingTerminalClosure
            )
        );
      }

      if (requirementsObtained.length > 0) {
        return requirementsObtained.every((requirement) => requirement === true);
      }

      return false;
    });
  };

  useEffect(() => {
    const subscription = watch((value: any, { name, type }) => {
      if (type === FIELD_CHANGE_EVENT_TYPE) {
        setUnloadingTerminalCalls([
          ...new Set(filterTerminalUnloadingCalls(value)),
          ...initialUnloadingTerminalCalls,
        ]);
        setLoadingTerminalCalls([
          ...new Set(filterTerminalLoadingCalls(value)),
          ...initialLoadingTerminalCalls,
        ]);
      }
    });

    return () => subscription.unsubscribe();
  }, [
    watch,
    terminalCalls,
    parentVessel,
    initialLoadingTerminalCalls,
    initialUnloadingTerminalCalls,
  ]);

  const isEmpty = watch("empty");
  const selectedType = watch("containerType");

  useEffect(() => {
    if (isEmpty && selectedType) {
      const typeId = selectedType["@id"] ?? selectedType;
      const ct = containerTypes.find((ct: any) => {
        const ctId = ct["@id"] ?? ct.id;
        return ctId.toString() === typeId.toString();
      });

      if (ct?.emptyWeight != null) {
        setValue("weight", ct.emptyWeight);
      }
    }
  }, [isEmpty, selectedType, containerTypes, setValue]);

  const onSubmit: SubmitHandler<Container> = (formData: any) => {
    if (formData.loadingTerminalCall && !formData.loadingTerminalCall["@id"]) {
      formData = {
        ...formData,
        loadingTerminal: findObjectById(
          terminalCalls,
          formData.loadingTerminalCall
        ).terminal,
      };
    }

    if (
      formData.unloadingTerminalCall &&
      !formData.unloadingTerminalCall["@id"]
    ) {
      formData = {
        ...formData,
        unloadingTerminal: findObjectById(
          terminalCalls,
          formData.unloadingTerminalCall
        ).terminal,
      };
    }

    if (container) {
      dispatch(updateSelectedContainer(formData));
    } else {
      dispatch(createContainer(formData, callback));
    }
  };

  const handleCloseForm = () => {
    if (childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  };

  const TerminalCallAlert: React.FC<{
    prefix: "loaded" | "unloaded";
    terminalName: string;
    date: string | Date;
  }> = ({ prefix, terminalName, date }) => {
    const action =
      prefix === "loaded" ? t("containerLoadedAt") : t("containerUnloadedAt");

    return (
      <Alert
        severity="success"
        icon={<CheckCircleIcon fontSize="small" />}
        sx={{
          display: "flex",
          alignItems: "center",
          height: 40,
          py: 0,
          px: 1,
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            py: 0,
            px: 0,
            width: "100%",
          },
          fontSize: "0.875rem",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          justifyContent="space-between"
        >
          <Typography noWrap>
            {action} {terminalName}
          </Typography>
          <Typography variant="caption" noWrap>
            {formatDateToString(date)}
          </Typography>
        </Box>
      </Alert>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <MuiContainer>
        <Grid container>
          <Grid item xs={12} className="form-fields-container">
            <Grid item xs={6} mb={2}>
              <SelectField
                name="voyage"
                label={t("voyage")}
                control={control}
                options={GetVoyageOptions(voyages)}
              />
            </Grid>
            <Grid item xs={6} mb={2} display="flex" justifyContent="center">
              <CheckboxField
                name="empty"
                label={t("markContainerAsEmpty")}
                control={control}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Typography variant="h6" className="form-fields-title">
              {t("information")}
            </Typography>
            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={6}>
                <InputTextField
                  name="uid"
                  label="UID"
                  control={control}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <AutocompleteField
                  name="containerType"
                  label={t("containerType")}
                  control={control}
                  options={containerTypes}
                  onCreateClick={() =>
                    dispatch(
                      showChildModal({
                        title: `containerType quickadd`,
                        description: "Add containerType",
                        form: "containerType",
                        formProps: {
                          callback: setValue,
                        },
                      })
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <NumberField
                  name="weight"
                  label={t("weightInKilo")}
                  control={control}
                  required
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              {t("trajectory")}
            </Typography>
            <Grid item xs={12} sm={12} className="form-fields-container">
              <Grid
                item
                xs={12}
                sm={6}
                className={`${container && container.loadedDate ? "disabled" : ""}`}
              >
                <SelectField
                  name="loadingTerminal"
                  label={t("loadingTerminal")}
                  control={control}
                  options={GetTerminalOptions(terminals)}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                className={`${container && container.unloadedDate ? "disabled" : ""}`}
              >
                <SelectField
                  name="unloadingTerminal"
                  label={t("unloadingTerminal")}
                  control={control}
                  options={GetTerminalOptions(terminals)}
                />
              </Grid>
            </Grid>
            <Grid display="flex">
              <Grid item xs={12} sm={6} className="form-fields-container">
                <Grid item xs={12} sm={6} className="form-field-datetime">
                  <DateTimeField
                    name="loadingTerminalClosure"
                    label={t("loadingTerminalClosure")}
                    control={control}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} className="form-fields-container">
                <Grid item xs={12} sm={6} className="form-field-datetime">
                  <DateTimeField
                    name="unloadingTerminalClosure"
                    label={t("unloadingTerminalClosure")}
                    control={control}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              {t("bargeBooking")}
            </Typography>
            <Grid item xs={12} sm={12} className="form-fields-container">
              <Grid item xs={12} sm={3}>
                <SelectField
                  name="vessel"
                  label={t("vessel")}
                  control={control}
                  options={GetVesselOptions(vessels, false)}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} className="form-fields-container">
              <Grid item xs={12} sm={6}>
                {container &&
                container.loadedDate &&
                container.loadingTerminalCall ? (
                  <TerminalCallAlert
                    prefix="loaded"
                    terminalName={container.loadingTerminalCall.terminal.name}
                    date={container.loadedDate}
                  />
                ) : (
                  <SelectField
                    name="loadingTerminalCall"
                    label={t("loadingTerminalCall")}
                    control={control}
                    options={GetTerminalCallOptions(loadingTerminalCalls)}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {container &&
                container.unloadedDate &&
                container.unloadingTerminalCall ? (
                  <TerminalCallAlert
                    prefix="unloaded"
                    terminalName={container.unloadingTerminalCall.terminal.name}
                    date={container.unloadedDate}
                  />
                ) : (
                  <SelectField
                    name="unloadingTerminalCall"
                    label={t("unloadingTerminalCall")}
                    control={control}
                    options={GetTerminalCallOptions(unloadingTerminalCalls)}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              {t("labels")}
            </Typography>
            <Grid item xs={12} sm={12} className="form-fields-container">
              <Grid item xs={12} sm={12}>
                <SelectMultipleField
                  name="labels"
                  label={t("labels")}
                  control={control}
                  options={GetLabelOptions(labels)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>

          <MultipleFileField
            control={control}
            register={register}
            setValue={setValue}
            name="fileGroup.files"
            existingFiles={
              container && container.fileGroup ? container.fileGroup.files : []
            }
          />
        </Grid>
      </MuiContainer>
      <Grid item xs={12} className="button-wrapper">
        <Grid container spacing={1}>
          <Grid item>
            <Button
              onClick={handleCloseForm}
              disabled={isSubmitting}
              variant="contained"
            >
              <CloseIcon fontSize="inherit" />
              &nbsp;{t("close")}
            </Button>
          </Grid>
          {container && (
            <Grid item>
              <Button
                onClick={() => {
                  dispatch(removeContainer(container));
                }}
                disabled={isSubmitting}
                variant="contained"
                color="error"
              >
                <DeleteIcon fontSize="inherit" />
                &nbsp;{t("delete")}
              </Button>
            </Grid>
          )}
        </Grid>
        <Button type="submit" disabled={isSubmitting} variant="contained">
          <SaveIcon fontSize="inherit" />
          &nbsp;{t("save")}
        </Button>
      </Grid>
    </form>
  );
};

export default ContainerForm;